import React, { forwardRef } from 'react';
import { isDefined } from '@nfctron/nexus-core';
import { useLogger } from '@nfctron/nexus-react';

import { VirtualMap } from '../VirtualMap/VirtualMap';
import { SvgRenderer } from './internal/SvgRenderer';
import { SvgSymbols } from './internal/SvgSymbols';
import { useMapData } from './internal/useMapData';
import { useSvgParser } from './internal/useSvgParser';
import type { SectorMapTypes as Types } from './SectorMap.types';

/**
 * SectorMap component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const SectorMap = forwardRef<SVGSVGElement, Types.Props>((props, ref) => {
	const {
		sector,
		categories,
		width,
		height,
		background = '#292929',
		forceBackground,
		onCrowdSelect,
		onCrowdUnselect,
		getCrowdProps,
		onSeatSelect,
		onSeatUnselect,
		getSeatProps,
		...rest
	} = props;
	const { loggerLow } = useLogger('SectorMap', { medium: true, high: true });

	const { parsedData, error } = useSvgParser(sector.drawing, width, height, background, forceBackground);
	const { seatMap, crowdMap, categoryMap } = useMapData(sector.seats, sector.crowds, categories);

	/** error handling */
	if (isDefined(error) || !isDefined(parsedData)) {
		loggerLow('Failed to parse SVG:', error ?? 'no data');
		return null;
	}

	return (
		<VirtualMap
			ref={ref}
			width={width}
			height={height || (parsedData.baseHeight / parsedData.baseWidth) * width}
			fill={parsedData.background}
			baseWidth={parsedData.baseWidth}
			baseHeight={parsedData.baseHeight}
			{...rest}
		>
			{/* svg symbols */}
			<SvgSymbols />
			{/* svg renderer */}
			<SvgRenderer
				root={parsedData.root}
				seatMap={seatMap}
				crowdMap={crowdMap}
				categoryMap={categoryMap}
				onCrowdSelect={onCrowdSelect}
				onCrowdUnselect={onCrowdUnselect}
				getCrowdProps={getCrowdProps}
				onSeatSelect={onSeatSelect}
				onSeatUnselect={onSeatUnselect}
				getSeatProps={getSeatProps}
			/>
		</VirtualMap>
	);
});

/** SectorMap component display name */
SectorMap.displayName = 'component.SectorMap';
